import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Chrome, Eye, EyeOff, Lock, Mail, Phone } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { authService } from '../services/authService'

export default function Login() {
  const navigate = useNavigate()
  const { login, loginWithPhone } = useAuth()

  const [step, setStep] = useState('welcome')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [showPhoneModal, setShowPhoneModal] = useState(false)
  const [phoneStep, setPhoneStep] = useState('phone')
  const [phoneLoading, setPhoneLoading] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [phoneData, setPhoneData] = useState({
    telefono: '',
    otp: ''
  })

  useEffect(() => {
    if (!toastMessage) return
    const timer = window.setTimeout(() => setToastMessage(''), 1800)
    return () => window.clearTimeout(timer)
  }, [toastMessage])

  const handleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value
    }))
  }

  const showToast = (message) => setToastMessage(message)

  const normalizePhone = (value) => {
    const raw = String(value || '').trim()
    if (!raw) return ''
    const keep = raw.replace(/[^\d+]/g, '')
    if (keep.startsWith('+')) return keep
    // Fallback país local del diseño de referencia.
    return `+598${keep}`
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)
    setError('')
    try {
      await login(formData.email, formData.password)
      navigate('/')
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión')
    } finally {
      setLoading(false)
    }
  }

  const handleSendOtp = async () => {
    const telefono = normalizePhone(phoneData.telefono)
    if (!telefono) {
      setPhoneError('Ingresa un teléfono válido')
      return
    }
    try {
      setPhoneLoading(true)
      setPhoneError('')
      await authService.sendOtp(telefono)
      setPhoneData((prev) => ({ ...prev, telefono }))
      setPhoneStep('otp')
      showToast('Código enviado por SMS')
    } catch (err) {
      setPhoneError(err.message || 'No se pudo enviar el código OTP')
    } finally {
      setPhoneLoading(false)
    }
  }

  const handlePhoneLogin = async () => {
    const telefono = normalizePhone(phoneData.telefono)
    const otp = String(phoneData.otp || '').trim()
    if (!telefono || !otp) {
      setPhoneError('Completa teléfono y código OTP')
      return
    }
    try {
      setPhoneLoading(true)
      setPhoneError('')
      await loginWithPhone(telefono, otp)
      showToast('Sesión iniciada con teléfono')
      setShowPhoneModal(false)
      navigate('/')
    } catch (err) {
      setPhoneError(err.message || 'No se pudo iniciar sesión con OTP')
    } finally {
      setPhoneLoading(false)
    }
  }

  const closePhoneModal = () => {
    setShowPhoneModal(false)
    setPhoneStep('phone')
    setPhoneError('')
    setPhoneLoading(false)
    setPhoneData({ telefono: '', otp: '' })
  }

  return (
    <div className="min-h-screen bg-[#fdf8f2] relative overflow-hidden px-4 py-6">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_110%,#e8d5bb_0%,#fdf8f2_65%)] pointer-events-none" />

      <div className="relative z-10 min-h-[calc(100vh-48px)] flex items-center justify-center">
        <div className="w-full max-w-[360px] bg-transparent">
          {step === 'welcome' ? (
            <div className="text-center px-2">
              <h1 className="text-[34px] font-black tracking-[-0.02em] text-[#5a3e28]">
                artemis<span className="text-[#c9883a]">.</span>
              </h1>
              <p className="text-[12px] text-[#a08060] mt-1">Tu compañero veterinario</p>

              <div className="relative mx-auto mt-4 w-[180px] h-[165px]">
                <div className="absolute top-2 -left-2 bg-white rounded-[14px] rounded-bl-[4px] px-3 py-1.5 text-[14px] font-bold text-[#5a3e28] shadow-[0_2px_10px_rgba(0,0,0,.1)]">
                  ¡Miau! 🐾
                </div>
                <div className="absolute bottom-0 inset-x-0 mx-auto h-[145px] w-[145px] rounded-full bg-[#f0e6d0] flex items-center justify-center text-[72px]">
                  🐱
                </div>
              </div>

              <div className="mt-2 space-y-2.5">
                <button
                  type="button"
                  onClick={() => setStep('form')}
                  className="w-full h-12 rounded-[14px] bg-[#7a6248] text-white text-[13px] font-bold inline-flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  INGRESAR CON EMAIL
                </button>
                <button
                  type="button"
                  onClick={() => showToast('Google — inicio social')}
                  className="w-full h-12 rounded-[14px] bg-white border border-[#e8e0d4] text-[#333] text-[13px] font-bold inline-flex items-center justify-center gap-2"
                >
                  <Chrome className="w-4 h-4" />
                  INGRESAR CON GOOGLE
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPhoneModal(true)
                    setPhoneError('')
                    setPhoneStep('phone')
                  }}
                  className="w-full h-12 rounded-[14px] bg-[#f0ebe3] text-[#5a3e28] text-[13px] font-bold inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  INGRESAR CON TELÉFONO
                </button>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <span className="h-px flex-1 bg-[#e8ddd0]" />
                <span className="text-[11px] text-[#c0a888]">o</span>
                <span className="h-px flex-1 bg-[#e8ddd0]" />
              </div>

              <p className="mt-3 text-[11px] leading-6 text-[#b0907a]">
                ¿No tenés cuenta?{' '}
                <Link to="/register" className="text-[#7a6248] font-bold">
                  Registrate
                </Link>
                <br />
                Al continuar aceptás los <span className="text-[#7a6248] font-bold">Términos</span> &amp;{' '}
                <span className="text-[#7a6248] font-bold">Privacidad</span>
              </p>
            </div>
          ) : (
            <div className="px-1">
              <button
                type="button"
                onClick={() => {
                  setStep('welcome')
                  setError('')
                }}
                className="h-8 w-8 rounded-full text-[#7a6248] inline-flex items-center justify-center"
                aria-label="Volver"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <h2 className="text-[24px] font-extrabold text-[#5a3e28] mt-2">Iniciar sesión</h2>
              <p className="text-[11px] text-[#a08060] font-mono mt-1">Acceso con credenciales</p>

              <div className="mx-auto mt-3 mb-2 w-[132px] h-[96px] rounded-2xl bg-[#f0e6d0] flex items-center justify-center text-[52px]">
                🙈
              </div>

              <form onSubmit={handleSubmit} className="mt-4">
                {error ? (
                  <div className="mb-3 rounded-xl bg-[#fde8e8] border border-[#f4caca] px-3 py-2 text-[12px] text-[#b23a3a]">
                    {error}
                  </div>
                ) : null}

                <div className="relative mb-3">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#b0907a]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                    autoComplete="email"
                    required
                    className="w-full h-12 rounded-[14px] border border-[#e8ddd0] bg-white pl-10 pr-3 text-[14px] text-[#333] outline-none focus:border-[#c9883a] focus:ring-2 focus:ring-[#c9883a]/15"
                  />
                </div>

                <div className="relative mb-3">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#b0907a]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Contraseña"
                    autoComplete="current-password"
                    required
                    className="w-full h-12 rounded-[14px] border border-[#e8ddd0] bg-white pl-10 pr-10 text-[14px] text-[#333] outline-none focus:border-[#c9883a] focus:ring-2 focus:ring-[#c9883a]/15"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9f9f9f]"
                    aria-label="Mostrar u ocultar contraseña"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-[14px] bg-[#c9883a] text-white text-[14px] font-bold disabled:opacity-60"
                >
                  {loading ? 'Conectando...' : 'INGRESAR'}
                </button>

                <div className="grid grid-cols-2 gap-2 mt-2.5">
                  <button
                    type="button"
                    onClick={() => showToast('Google — inicio social')}
                    className="h-10 rounded-[12px] border border-[#e8ddd0] bg-white text-[12px] font-semibold text-[#555] inline-flex items-center justify-center gap-1.5"
                  >
                    <Chrome className="w-3.5 h-3.5" />
                    Google
                  </button>
                  <button
                    type="button"
                    onClick={() => showToast('Apple — inicio social')}
                    className="h-10 rounded-[12px] border border-[#e8ddd0] bg-white text-[12px] font-semibold text-[#555] inline-flex items-center justify-center gap-1.5"
                  >
                    <span className="text-sm leading-none"></span>
                    Apple
                  </button>
                </div>

                <div className="text-center mt-3">
                  <Link to="/forgot-password" className="text-[11px] text-[#a08060]">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>

                <div className="text-center mt-2">
                  <p className="text-[11px] text-[#a08060]">
                    ¿No tienes cuenta?{' '}
                    <Link to="/register" className="text-[#c9883a] font-semibold">
                      Regístrate
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {showPhoneModal ? (
        <div className="fixed inset-0 z-[120] bg-black/35 flex items-end justify-center p-3">
          <div className="w-full max-w-[360px] rounded-t-[22px] bg-white shadow-2xl">
            <div className="bg-[#7a6248] rounded-t-[22px] px-4 py-3 flex items-center justify-between">
              <h3 className="text-white text-[15px] font-bold">
                {phoneStep === 'phone' ? 'Ingresar con teléfono' : 'Validar código OTP'}
              </h3>
              <button
                type="button"
                onClick={closePhoneModal}
                className="text-white/80 text-xl leading-none"
                aria-label="Cerrar modal"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              {phoneStep === 'phone' ? (
                <>
                  <label className="block text-[12px] font-semibold text-[#555] mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={phoneData.telefono}
                    onChange={(event) => setPhoneData((prev) => ({ ...prev, telefono: event.target.value }))}
                    placeholder="+598 99 123 456"
                    className="w-full h-11 rounded-[11px] border border-[#e8e8e8] bg-white px-3 text-[13px] outline-none focus:border-[#7a9a52]"
                  />
                  <p className="text-[10px] text-[#8a8a8a] mt-1">Solicitud de código OTP</p>
                  {phoneError ? <p className="text-[12px] text-[#b23a3a] mt-2">{phoneError}</p> : null}
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={phoneLoading}
                    className="w-full h-11 rounded-[14px] bg-[#7a9a52] text-white text-[14px] font-bold mt-3 disabled:opacity-60"
                  >
                    {phoneLoading ? 'Enviando...' : 'Enviar código'}
                  </button>
                </>
              ) : (
                <>
                  <label className="block text-[12px] font-semibold text-[#555] mb-1">Código OTP</label>
                  <input
                    type="text"
                    value={phoneData.otp}
                    onChange={(event) => setPhoneData((prev) => ({ ...prev, otp: event.target.value }))}
                    placeholder="123456"
                    className="w-full h-11 rounded-[11px] border border-[#e8e8e8] bg-white px-3 text-[13px] outline-none focus:border-[#7a9a52]"
                  />
                  <p className="text-[10px] text-[#8a8a8a] mt-1">Validación de acceso por teléfono</p>
                  {phoneError ? <p className="text-[12px] text-[#b23a3a] mt-2">{phoneError}</p> : null}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setPhoneStep('phone')}
                      className="h-11 rounded-[14px] border border-[#e8e8e8] text-[#666] text-[13px] font-semibold"
                    >
                      Volver
                    </button>
                    <button
                      type="button"
                      onClick={handlePhoneLogin}
                      disabled={phoneLoading}
                      className="h-11 rounded-[14px] bg-[#7a9a52] text-white text-[13px] font-bold disabled:opacity-60"
                    >
                      {phoneLoading ? 'Validando...' : 'Ingresar'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {toastMessage ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-[#111] text-white text-[12px] px-4 py-2 shadow-lg z-[100]">
          {toastMessage}
        </div>
      ) : null}
    </div>
  )
}
